import { useState } from "react";
import { Crown, TrendingDown, Info, Sparkles } from "lucide-react";
import { fmtKc, localize } from "@/data/vehicles";
import { useLang } from "@/i18n/LanguageContext";

const ROWS = [
  { key: "year", label: "compare.rowYear", ours: (v) => v.year, theirs: (c) => c.year, better: (a, b) => a > b },
  { key: "powerKw", label: "compare.rowPower", ours: (v) => v.powerKw, theirs: (c) => c.powerKw, fmt: (x) => `${x} kW`, better: (a, b) => a > b },
  { key: "mileage", label: "compare.rowMileage", ours: (v, lang) => localize(v.mileage, lang), theirs: (c) => c.mileage },
  { key: "trim", label: "compare.rowTrim", ours: (v, lang) => (v.trim ? localize(v.trim, lang) : null), theirs: (c) => c.trim || c.note },
  { key: "term", label: "compare.rowTerm", ours: (v, lang, t) => t("compare.noTerm"), theirs: (c) => c.term, oursGood: true },
  { key: "deposit", label: "compare.rowDeposit", ours: (v, lang, t) => t("compare.noDeposit"), theirs: (c) => c.deposit, oursGood: true },
  { key: "kmIncluded", label: "compare.rowKm", ours: (v, lang, t) => t("compare.kmOurs"), theirs: (c) => c.kmIncluded },
];

const Verdict = ({ vehicle, rentals, t }) => {
  const prices = rentals.map((c) => c.price);
  if (!prices.length) return null;
  const avg = Math.round(prices.reduce((a, b) => a + b, 0) / prices.length);
  const diff = avg - vehicle.priceMonthly;
  if (diff > 0) {
    return (
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 bg-emerald-500/10 border border-emerald-500/25 text-emerald-300 text-sm font-semibold px-4 py-2.5 rounded-2xl" data-testid="compare-verdict-save">
        <TrendingDown className="w-4 h-4" /> {t("compare.saveAvg")} {fmtKc(diff)} Kč/měs <span className="text-emerald-400/60 font-normal text-xs">({t("compare.avg")} {fmtKc(avg)} Kč)</span>
      </div>
    );
  }
  return (
    <div className="flex items-start gap-2 bg-orange-500/10 border border-orange-500/25 text-orange-200 text-sm px-4 py-2.5 rounded-2xl" data-testid="compare-verdict-value">
      <Sparkles className="w-4 h-4 mt-0.5 shrink-0 text-orange-400" />
      <span><strong>{t("compare.pricier")} {fmtKc(-diff)} Kč</strong> {t("compare.pricierTail")}</span>
    </div>
  );
};

// Plain-language "what you get for the price" line for each competitor offer vs. our car.
const explainDiff = (vehicle, c, t, lang) => {
  const reasons = [];
  if (vehicle.year && c.year) {
    if (c.year < vehicle.year) reasons.push(`${t("compare.whyOlder")} (${c.year} vs. ${vehicle.year} ${t("compare.whyOurs")})`);
    else if (c.year > vehicle.year) reasons.push(`${t("compare.whyNewer")} (${c.year})`);
  }
  if (vehicle.powerKw && c.powerKw) {
    if (c.powerKw < vehicle.powerKw) reasons.push(`${t("compare.whyWeaker")} (${c.powerKw} vs. ${vehicle.powerKw} kW)`);
    else if (c.powerKw > vehicle.powerKw) reasons.push(`${t("compare.whyStronger")} (${c.powerKw} kW)`);
  }
  const ourTrim = vehicle.trim ? localize(vehicle.trim, lang) : null;
  if (ourTrim && c.trim && c.trim.toLowerCase() !== ourTrim.toLowerCase()) reasons.push(`${t("compare.whyTrim")}: ${c.trim}`);
  if (c.term && !/bez|none|keine|flexib/i.test(c.term)) reasons.push(`${t("compare.whyTerm")} ${c.term}`);
  if (c.deposit && !/^0|bez|none|keine/i.test(c.deposit)) reasons.push(`${t("compare.whyDeposit")} ${c.deposit}`);
  if (c.kmIncluded && /rok|year|jahr/i.test(c.kmIncluded)) {
    const n = parseInt(String(c.kmIncluded).replace(/\s/g, ""), 10);
    if (n && n / 12 < 3500) reasons.push(`${t("compare.whyKm")} (${c.kmIncluded})`);
  }
  return reasons;
};

const WhyList = ({ vehicle, list, t, lang }) => (
  <div className="mt-5 rounded-2xl bg-white/[0.03] border border-white/[0.06] p-4" data-testid="compare-why">
    <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-3">{t("compare.whyTitle")}</p>
    <ul className="space-y-2">
      {list.map((c, i) => {
        const d = c.price - vehicle.priceMonthly;
        const reasons = explainDiff(vehicle, c, t, lang);
        return (
          <li key={`${c.name}-${i}`} className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-sm" data-testid={`compare-why-${i}`}>
            <span className="font-semibold text-zinc-100">{c.name}</span>
            <span className={`text-xs font-semibold ${d > 0 ? "text-emerald-400" : d < 0 ? "text-red-400/80" : "text-zinc-500"}`}>
              {d > 0 ? `${t("compare.whyPricier")} ${fmtKc(d)} Kč` : d < 0 ? `${t("compare.whyCheaper")} ${fmtKc(-d)} Kč` : t("compare.whySame")}
            </span>
            <span className="text-zinc-400">{reasons.length ? `· ${reasons.join(" · ")}` : `· ${t("compare.whyNoDiff")}`}</span>
          </li>
        );
      })}
    </ul>
  </div>
);

// Side-by-side comparison of our car vs. each competitor offer: price next to year, power, mileage, trim, commitment.
export const CompareTable = ({ vehicle }) => {
  const { lang, t } = useLang();
  const comps = (vehicle.competitors || []).filter((c) => c.price > 0);
  const rentals = comps.filter((c) => c.type !== "lease").sort((a, b) => a.price - b.price);
  const leases = comps.filter((c) => c.type === "lease").sort((a, b) => a.price - b.price);
  const [tabState, setTab] = useState(null);
  if (!comps.length || vehicle.priceMonthly == null) return null;
  const tab = tabState ?? (rentals.length ? "rental" : "lease");
  const list = tab === "lease" ? leases : rentals;
  const cols = [{ ours: true }, ...list];
  const dash = t("compare.unknown");

  return (
    <section className="glass rounded-3xl p-5 sm:p-7" data-testid="detail-comparison">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <p className="text-xs uppercase tracking-wider text-orange-400 mb-1">{t("compare.title")}</p>
          <p className="text-zinc-400 text-sm max-w-xl">{t("compare.subtitle")}</p>
        </div>
        {leases.length > 0 && rentals.length > 0 && (
          <div className="inline-flex rounded-full border border-white/10 bg-white/[0.04] p-1 text-xs font-semibold">
            <button type="button" onClick={() => setTab("rental")} className={`px-3.5 py-1.5 rounded-full transition-colors ${tab === "rental" ? "bg-white text-black" : "text-zinc-400 hover:text-white"}`} data-testid="compare-tab-rental">{t("compare.tabRentals")} ({rentals.length})</button>
            <button type="button" onClick={() => setTab("lease")} className={`px-3.5 py-1.5 rounded-full transition-colors ${tab === "lease" ? "bg-white text-black" : "text-zinc-400 hover:text-white"}`} data-testid="compare-tab-lease">{t("compare.tabLeases")} ({leases.length})</button>
          </div>
        )}
      </div>

      <div className="overflow-x-auto -mx-5 sm:mx-0 px-5 sm:px-0 no-scrollbar">
        <table className="min-w-[640px] w-full text-sm border-separate border-spacing-0" data-testid="compare-table">
          <thead>
            <tr>
              <th className="text-left font-normal text-zinc-500 text-xs pb-3 pr-3 w-36 align-bottom" />
              {cols.map((c, i) => (
                <th key={i} className={`text-left pb-3 px-3 align-bottom min-w-[150px] ${c.ours ? "rounded-t-2xl bg-orange-500/10 border-x border-t border-orange-500/30" : ""}`}>
                  {c.ours ? (
                    <span className="inline-flex items-center gap-1.5 font-['Cabinet_Grotesk'] font-bold text-orange-200 text-base pt-3"><Crown className="w-4 h-4 text-orange-400" /> EffectDeal</span>
                  ) : (
                    <span className="block">
                      <span className="font-['Cabinet_Grotesk'] font-bold text-zinc-200 text-base block truncate" title={c.name}>{c.name}</span>
                      {c.note && <span className="text-zinc-500 text-[11px] block truncate" title={c.note}>{c.note}</span>}
                    </span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="py-2.5 pr-3 text-zinc-400 border-b border-white/5">{t("compare.rowPrice")}</td>
              {cols.map((c, i) => (
                <td key={i} className={`py-2.5 px-3 border-b border-white/5 font-bold whitespace-nowrap ${c.ours ? "bg-orange-500/10 border-x border-orange-500/30 text-orange-400 text-lg" : "text-zinc-100"}`}>
                  {fmtKc(c.ours ? vehicle.priceMonthly : c.price)} Kč
                </td>
              ))}
            </tr>
            {ROWS.map((row) => {
              const ourRaw = row.ours(vehicle, lang, t);
              if (ourRaw == null && !list.some((c) => row.theirs(c) != null)) return null;
              return (
                <tr key={row.key}>
                  <td className="py-2.5 pr-3 text-zinc-400 border-b border-white/5">{t(row.label)}</td>
                  {cols.map((c, i) => {
                    const val = c.ours ? ourRaw : row.theirs(c);
                    const theirRaw = c.ours ? null : row.theirs(c);
                    const win = !c.ours && row.better && ourRaw != null && theirRaw != null && row.better(ourRaw, theirRaw);
                    return (
                      <td key={i} className={`py-2.5 px-3 border-b border-white/5 ${c.ours ? `bg-orange-500/10 border-x border-orange-500/30 ${row.oursGood ? "text-emerald-300 font-semibold" : "text-zinc-100"}` : win ? "text-zinc-500" : "text-zinc-300"}`}>
                        {val == null || val === "" ? <span className="text-zinc-600">{dash}</span> : row.fmt ? row.fmt(val) : val}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
            <tr>
              <td className="py-2.5 pr-3 text-zinc-400">{t("compare.rowDiff")}</td>
              {cols.map((c, i) => {
                if (c.ours) return <td key={i} className="py-2.5 px-3 bg-orange-500/10 border-x border-b border-orange-500/30 rounded-b-2xl" />;
                const d = c.price - vehicle.priceMonthly;
                return (
                  <td key={i} className={`py-2.5 px-3 font-semibold whitespace-nowrap ${d > 0 ? "text-emerald-400" : d < 0 ? "text-red-400/80" : "text-zinc-500"}`} data-testid={`compare-diff-${i}`}>
                    {d > 0 ? `+${fmtKc(d)} Kč` : d < 0 ? `−${fmtKc(-d)} Kč` : "="}
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>

      <div className="mt-5 space-y-3">
        {tab === "rental" ? <Verdict vehicle={vehicle} rentals={rentals} t={t} /> : (
          <p className="flex items-start gap-2 text-sm text-zinc-300 bg-white/[0.04] border border-white/10 rounded-2xl px-4 py-2.5" data-testid="compare-lease-hint"><Info className="w-4 h-4 mt-0.5 shrink-0 text-orange-400" /> {t("compare.leaseHint")}</p>
        )}
        {vehicle.compareVerdict && (
          <p className="text-sm text-zinc-200 leading-relaxed" data-testid="compare-admin-verdict">{localize(vehicle.compareVerdict, lang)}</p>
        )}
        <WhyList vehicle={vehicle} list={list} t={t} lang={lang} />
        <p className="text-zinc-600 text-[11px]">
          {t("compare.disclaimer")}
          {vehicle.competitorsUpdatedAt ? ` ${t("compare.updated")} ${new Date(vehicle.competitorsUpdatedAt).toLocaleDateString("cs-CZ")}.` : ""}
        </p>
      </div>
    </section>
  );
};

export default CompareTable;
